import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SESSION_APP_FALLBACK_TOKEN,
  isSessionAppFallbackHost,
  getSessionLandingPath,
} from '../config/sessionAppData';

export const SKIN_SESSION_TOKEN_KEY = 'skin_session_token';
const STORAGE_KEY = SKIN_SESSION_TOKEN_KEY;
/** Persists the exact `/session/...` path when the user arrived with a token in the URL (survives refresh on other routes). */
const HOME_PATH_KEY = 'skin_session_home_path';

const SkinSessionContext = createContext(null);

function readInitialPathHomeFromSessionUrl() {
  if (typeof window === 'undefined') return null;
  const pathname = window.location.pathname;
  const m = /^\/session\/([^/]+)/.exec(pathname);
  if (m && m[1]) return pathname;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const homePath = sessionStorage.getItem(HOME_PATH_KEY);
    if (!stored || !homePath || !homePath.startsWith('/session/')) return null;
    const seg = decodeURIComponent(homePath.slice('/session/'.length).split('/')[0]);
    if (seg === String(stored).trim()) return homePath;
  } catch (_) {}
  return null;
}

export function SkinSessionProvider({ children }) {
  const location = useLocation();
  const [pathHomeFromSessionUrl, setPathHomeFromSessionUrl] = useState(readInitialPathHomeFromSessionUrl);
  const [token, setTokenState] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  const setToken = useCallback((value) => {
    const next = typeof value === 'string' ? value : '';
    setTokenState(next);
    if (!next) {
      setPathHomeFromSessionUrl(null);
      try {
        sessionStorage.removeItem(HOME_PATH_KEY);
      } catch (_) {}
    }
    try {
      if (next) {
        sessionStorage.setItem(STORAGE_KEY, next);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (_) {}
  }, []);

  const sessionLandingPath = useMemo(() => {
    if (pathHomeFromSessionUrl) return pathHomeFromSessionUrl;
    return getSessionLandingPath(token);
  }, [pathHomeFromSessionUrl, token]);

  useEffect(() => {
    const match = /^\/session\/([^/]+)/.exec(location.pathname);
    if (match && match[1]) {
      const fromPath = decodeURIComponent(match[1]);
      if (fromPath) {
        setToken(fromPath);
        setPathHomeFromSessionUrl(location.pathname);
        try {
          sessionStorage.setItem(HOME_PATH_KEY, location.pathname);
        } catch (_) {}
      }
      return;
    }
    let stored = '';
    try {
      stored = sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (_) {}
    if (stored.trim()) return;
    if (!isSessionAppFallbackHost()) return;
    setTokenState((prev) => {
      if (prev && prev.trim()) return prev;
      try {
        sessionStorage.setItem(STORAGE_KEY, SESSION_APP_FALLBACK_TOKEN);
      } catch (_) {}
      return SESSION_APP_FALLBACK_TOKEN;
    });
  }, [location.pathname, setToken]);

  return (
    <SkinSessionContext.Provider value={{ token, setToken, sessionLandingPath }}>
      {children}
    </SkinSessionContext.Provider>
  );
}

export function useSkinSession() {
  const ctx = useContext(SkinSessionContext);
  if (!ctx) {
    throw new Error('useSkinSession must be used within SkinSessionProvider');
  }
  return ctx;
}

/** Same token resolution as SkinRoutinePage: URL/query → session context → sessionStorage → fallback host token. */
export function resolveAppSessionToken(...candidates) {
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (value) return value;
  }
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored && String(stored).trim()) return String(stored).trim();
  } catch (_) {}
  if (isSessionAppFallbackHost()) return SESSION_APP_FALLBACK_TOKEN;
  return '';
}
